import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import numeral from 'numeral'
import {
  activeDeliveryBuckets,
  DeliveryGroupingKey,
  DeliveryGroupRow,
  DeliveryOverviewResponse,
  fetchDeliveryOverview,
} from './deliveryContract'

/**
 * KAN-195 — Companion chart showing the Disney MLB / Disney Non-MLB / Non-Disney
 * split per category, in one of four interchangeable representations:
 *
 *   - 'proportional' : 100% stacked horizontal bars (bucket MIX per category)
 *   - 'stacked'      : absolute stacked horizontal bars (volume + mix)
 *   - 'grouped'      : clustered horizontal bars (exact bucket comparison)
 *   - 'heatmap'      : category × bucket grid, color = impressions
 *
 * Self-contained chart config (subView left blank so no baseChartSettings preset
 * is merged in). Pick a `mode`; everything else is shared.
 */
export default class DeliveryBucketMix extends SystemDashboardWidget {
  public title: string = 'Bucket Mix'

  public key: string = 'delivery-bucket-mix'

  public view: string = 'chart'

  public subView: string = ''

  public size = {
    lg: 8,
    md: 12,
  }

  public dataKey: DeliveryGroupingKey = 'by_industry'

  public mode: 'proportional' | 'stacked' | 'grouped' | 'heatmap' = 'stacked'

  // Dashboard-wide Non-Disney toggle (broadcast from the module's filter row).
  public filters: any = {
    show_nondisney: 'hide',
  }

  constructor(args: any = null) {
    super()

    if (args && args.title) this.title = args.title
    if (args && args.key) this.key = args.key
    if (args && args.dataKey) this.dataKey = args.dataKey
    if (args && args.mode) this.mode = args.mode
    if (args && args.size) this.size = args.size
  }

  public async dataSource(): Promise<DeliveryGroupRow[]> {
    const res: DeliveryOverviewResponse = await fetchDeliveryOverview(false, this.filters.month)
    return res[this.dataKey]
  }

  protected dataMapper(rows: DeliveryGroupRow[]): Promise<any> {
    const buckets = activeDeliveryBuckets(this.filters.show_nondisney === 'show')
    const labels = rows.map(row => row.label)
    const colors = buckets.map(b => b.color)

    // Match the companion breakdown table's height: its two header rows +
    // total row are roughly 160px and each body row ~42px, so size the chart
    // by category count. Never below the classic 350 — a one-row table would
    // otherwise squeeze the chart (legend + axis leave little plot area).
    const height = Math.min(1200, Math.max(350, 160 + rows.length * 42))

    if (this.mode === 'heatmap') {
      const series = buckets.map(bucket => ({
        name: bucket.label,
        data: rows.map(row => ({ x: row.label, y: row[bucket.key as keyof DeliveryGroupRow] })),
      }))

      this.chartSettings = {
        chart: {
          type: 'heatmap', height, toolbar: { show: false }, fontFamily: 'inherit',
        },
        colors: ['#2563EB'],
        dataLabels: { enabled: false },
        plotOptions: { heatmap: { shadeIntensity: 0.6, radius: 2 } },
        legend: { show: false },
        tooltip: { y: { formatter: (v: number) => numeral(v).format('0,0') } },
        total: 0,
        labels: [],
      }

      return Promise.resolve(series)
    }

    // Bar modes: one series per bucket, value per category.
    const series = buckets.map(bucket => ({
      name: bucket.label,
      data: rows.map(row => row[bucket.key as keyof DeliveryGroupRow] as number),
    }))

    const stacked = this.mode !== 'grouped'
    const proportional = this.mode === 'proportional'

    this.chartSettings = {
      chart: {
        type: 'bar',
        stacked,
        stackType: proportional ? '100%' : 'normal',
        height,
        toolbar: { show: false },
        fontFamily: 'inherit',
      },
      colors,
      plotOptions: { bar: { horizontal: true, borderRadius: 2, columnWidth: '70%' } },
      dataLabels: { enabled: false },
      // Clicking a bucket label in the legend toggles that series on/off and
      // restacks the bars — this is the "turn groups on/off" control (KAN-195).
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        onItemClick: { toggleDataSeries: true },
        onItemHover: { highlightDataSeries: true },
      },
      xaxis: {
        categories: labels,
        labels: {
          formatter: (v: number) =>
            (proportional ? numeral(v).format('0%') : numeral(v).format('0.0a')),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (v: number) => numeral(v).format('0,0') },
      },
      grid: { borderColor: '#eef0f3' },
      total: 0,
      labels: [],
    }

    return Promise.resolve(series)
  }
}
